/**
 * Add to Cart Flyout Action
 * Creates a flyout animation that slerps/animates from a trigger element to a target element
 * Triggered by: arc:action:addToCartFlyout
 */

export const runAddToCartFlyout = async (data = {}) => {
    try {
        const {
            triggerSelector = null,
            targetSelector = null,
            triggerElement = null,
            targetElement = null,
            content = '🛒',
            size = 30,
            duration = 800,
            easing = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            onComplete = null,
            className = 'flyout-element',
            backgroundColor = '#007bff',
            color = '#fff',
            borderRadius = '50%',
            zIndex = 10001,
            scale = { start: 1, mid: 1.2, end: 0.3 },
            debug = false
        } = data;

        if (debug) {
            console.log('Flyout Debug Info:', {
                triggerSelector,
                targetSelector,
                triggerElement: !!triggerElement,
                targetElement: !!targetElement
            });
        }

        // Get trigger and target elements
        const trigger = triggerElement || (triggerSelector ? document.querySelector(triggerSelector) : null);
        const target = targetElement || (targetSelector ? document.querySelector(targetSelector) : null);

        if (!trigger) {
            throw new Error(`Trigger element is required (provide triggerElement or triggerSelector). Selector provided: ${triggerSelector}`);
        }

        // If no target is found, create a default target (center top of screen)
        let targetRect;
        if (!target) {
            console.warn(`Target element not found with selector: ${targetSelector}. Using default position.`);
            targetRect = {
                left: window.innerWidth / 2,
                top: 50,
                width: 0,
                height: 0
            };
        } else {
            targetRect = target.getBoundingClientRect();
        }

        // Get positions
        const triggerRect = trigger.getBoundingClientRect();

        // Calculate start and end positions
        const startX = triggerRect.left + triggerRect.width / 2;
        const startY = triggerRect.top + triggerRect.height / 2;
        const endX = targetRect.left + targetRect.width / 2;
        const endY = targetRect.top + targetRect.height / 2;

        // Create flyout element
        const flyout = document.createElement('div');
        flyout.className = className;
        flyout.style.cssText = `
            position: fixed;
            left: ${startX - size/2}px;
            top: ${startY - size/2}px;
            width: ${size}px;
            height: ${size}px;
            background: ${backgroundColor};
            color: ${color};
            border-radius: ${borderRadius};
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: ${size * 0.6}px;
            font-weight: bold;
            z-index: ${zIndex};
            pointer-events: none;
            user-select: none;
            transform: scale(${scale.start});
            transition: all ${duration}ms ${easing};
        `;
        
        flyout.innerHTML = content;
        document.body.appendChild(flyout);

        // Create control point for curved animation (bezier-like path)
        const controlX = startX + (endX - startX) * 0.5;
        const controlY = Math.min(startY, endY) - 100; // Arc upward

        // Animation function using requestAnimationFrame for smooth interpolation
        const animate = () => {
            let startTime = null;

            const frame = (currentTime) => {
                if (startTime === null) startTime = currentTime;
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Easing function (cubic-bezier approximation)
                const easeProgress = cubicBezier(progress, 0.25, 0.46, 0.45, 0.94);

                // Quadratic bezier curve calculation for smooth path
                const t = easeProgress;
                const x = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * controlX + t * t * endX;
                const y = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * controlY + t * t * endY;

                // Scale animation (grow then shrink)
                let currentScale;
                if (progress < 0.3) {
                    // Grow phase
                    currentScale = scale.start + (scale.mid - scale.start) * (progress / 0.3);
                } else {
                    // Shrink phase
                    currentScale = scale.mid + (scale.end - scale.mid) * ((progress - 0.3) / 0.7);
                }

                // Apply position and scale
                flyout.style.left = `${x - size/2}px`;
                flyout.style.top = `${y - size/2}px`;
                flyout.style.transform = `scale(${currentScale})`;

                // Fade out near the end
                if (progress > 0.8) {
                    const fadeProgress = (progress - 0.8) / 0.2;
                    flyout.style.opacity = 1 - fadeProgress;
                }

                if (progress < 1) {
                    requestAnimationFrame(frame);
                } else {
                    // Animation complete
                    if (flyout.parentNode) {
                        flyout.parentNode.removeChild(flyout);
                    }
                    if (onComplete && typeof onComplete === 'function') {
                        onComplete();
                    }
                }
            };

            requestAnimationFrame(frame);
        };

        // Start animation
        animate();

        return {
            success: true,
            message: 'Flyout animation started',
            data: {
                trigger: triggerSelector || 'Element provided',
                target: targetSelector || target ? 'Element provided' : 'Default position used',
                duration,
                flyoutElement: flyout
            }
        };

    } catch (error) {
        console.error('Error running flyout animation:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Cubic bezier easing function
function cubicBezier(t, x1, y1, x2, y2) {
    // Approximation of cubic-bezier using Newton-Raphson method
    const cx = 3 * x1;
    const bx = 3 * (x2 - x1) - cx;
    const ax = 1 - cx - bx;
    
    const cy = 3 * y1;
    const by = 3 * (y2 - y1) - cy;
    const ay = 1 - cy - by;
    
    function sampleCurveX(t) {
        return ((ax * t + bx) * t + cx) * t;
    }
    
    function sampleCurveY(t) {
        return ((ay * t + by) * t + cy) * t;
    }
    
    function solveCurveX(x) {
        let t2 = x;
        for (let i = 0; i < 8; i++) {
            const x2 = sampleCurveX(t2) - x;
            if (Math.abs(x2) < 1e-6) break;
            const d2 = (3 * ax * t2 + 2 * bx) * t2 + cx;
            if (Math.abs(d2) < 1e-6) break;
            t2 = t2 - x2 / d2;
        }
        return t2;
    }
    
    return sampleCurveY(solveCurveX(t));
}

export default runAddToCartFlyout;

// Helper function for quick testing
export const testFlyout = (triggerSelector = 'body', targetSelector = null) => {
    return runAddToCartFlyout({
        triggerSelector,
        targetSelector,
        content: '✨',
        size: 40,
        duration: 1000,
        debug: true
    });
};

// Common cart selectors to try
export const commonCartSelectors = [
    '.cart-icon',
    '.cart-button',
    '[data-cart]',
    '.header-cart',
    '.cart-count',
    '.cart-counter',
    '#cart',
    '.shopping-cart',
    '.minicart',
    '.cart-link'
];